import { useCallback, useEffect, useRef } from 'react';

// Longest the frozen editor frame is held waiting for the saved view image before switching anyway
const SAVE_VIEW_LOAD_FALLBACK_MS = 3000;

/**
 * Coordinates the post-save hand-off from the (frozen) editor to the view image.
 *
 * After a save persists, `finishEditingAfterSave` holds the current mode until the view image has loaded the
 * newly-saved source (`handleViewImageLoaded`, called from the view's onload) — otherwise switching to view
 * mode immediately reveals the view still showing the previous orientation mid-load. A fallback timer still
 * completes the switch if that load never arrives.
 */
export const useFinishEditingAfterViewLoad = (finishEditing: () => void) => {
    const awaitingRef = useRef(false);
    const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleViewImageLoaded = useCallback(() => {
        if (!awaitingRef.current) return;
        awaitingRef.current = false;
        if (fallbackRef.current) clearTimeout(fallbackRef.current);
        finishEditing();
    }, [finishEditing]);

    const finishEditingAfterSave = useCallback(() => {
        awaitingRef.current = true;
        fallbackRef.current = setTimeout(handleViewImageLoaded, SAVE_VIEW_LOAD_FALLBACK_MS);
    }, [handleViewImageLoaded]);

    useEffect(() => () => clearTimeout(fallbackRef.current ?? undefined), []);

    return { finishEditingAfterSave, handleViewImageLoaded };
};
